import React, { FC, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import SimpleModal from "../Modals/SimpleModal";
import { constants } from "../../utils/constants/general";

interface SavePresetModalProps {
  open: boolean;
  saving: boolean;
  isNameTaken: (name: string) => boolean;
  onSubmit: (input: { name: string; setAsDefault: boolean }) => void;
  onClose: () => void;
}

const MAX_NAME_LEN = 60;

const SavePresetModal: FC<SavePresetModalProps> = ({
  open,
  saving,
  isNameTaken,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setSetAsDefault(false);
      setError(null);
    }
  }, [open]);

  const validate = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return "Name is required.";
    if (trimmed.length > MAX_NAME_LEN)
      return `Name must be ${MAX_NAME_LEN} characters or fewer.`;
    if (isNameTaken(trimmed))
      return "A preset with this name already exists.";
    return null;
  };

  const handleAccept = () => {
    const validationError = validate(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSubmit({ name: name.trim(), setAsDefault });
  };

  return (
    <SimpleModal
      open={open}
      handleClose={onClose}
      handleAccept={handleAccept}
      acceptLabel={"Save preset"}
      acceptButtonLoading={saving}
      acceptButtonDisabled={saving}
      title={"Save filter preset"}
      minWidth={"420px"}
      maxWidth={"480px"}
      zIndex={constants.zIndexes.drawerModal}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
        <Typography variant={"body2"} color={"text.secondary"}>
          Save the current filter selection so you can quickly apply it later.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size={"small"}
          label={"Preset name"}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          inputProps={{ maxLength: MAX_NAME_LEN }}
          error={Boolean(error)}
          helperText={error ?? `${name.trim().length}/${MAX_NAME_LEN}`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAccept();
            }
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              color={"secondary"}
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
            />
          }
          label={"Set as default (auto-apply on page open)"}
        />
      </Box>
    </SimpleModal>
  );
};

export default SavePresetModal;
